import React, { useMemo, useState, useEffect } from 'react';
import {
  Box, Typography, Button, Autocomplete, TextField, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Divider,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  ResponsiveContainer, ComposedChart, Area, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import {
  Clear as ClearIcon,
  History as HistoryIcon,
  Inventory2 as InventoryIcon,
  ShowChart as ShowChartIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { ProductDynamicsRow, JournalMovementRow } from '../../types';
import { rainbowColor, fmtSum } from '../../utils';
import { QtyTooltip } from './ChartTooltips';
import { API_BASE } from '../../constants';

// ─── Types & Helpers ──────────────────────────────────────────────────────────

interface ProductDynamicsChartProps {
  data: ProductDynamicsRow[];
  movements?: JournalMovementRow[];
  pharmacies: string[];
  products: string[];
  c3Pharmacy: string;
  setC3Pharmacy: (v: string) => void;
  c3Product: string | null;
  setC3Product: (v: string | null) => void;
}

const axisStyle = {
  tick: { fontSize: 11, fill: '#94a3b8' },
  axisLine: { stroke: '#e2e8f0' },
  tickLine: false,
};
const gridStyle = { stroke: '#f1f5f9', strokeDasharray: '4 4', vertical: false };

/** Форматирует упаковки и штуки (например: "0 уп. / 2 шт." или "5 уп.") */
function formatQtyPcs(qty: number, pcs?: number): string {
  const qStr = `${qty || 0} уп.`;
  return pcs && pcs > 0 ? `${qStr} / ${pcs} шт.` : qStr;
}

/** Тип нормализованной записи движения для графика */
interface MovementPoint {
  id: number | string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  label: string;
  actionDate: string;
  actionBy: string;
  pharmacyNum: string;
  cityId: string;
  quantity: number;
  quantityPcs: number;
  price: number;
  totalSum: number;
  serialNumber: string;
  expiryDate: string;
  producer: string;
  comment: string;
  val: number;  // значение для оси Y
}

/** Чистый информативный тултип для графика операций */
const MovementTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d: MovementPoint = payload[0].payload;

  const actionColor = d.action === 'CREATE' ? '#16a34a' : d.action === 'DELETE' ? '#dc2626' : '#2563eb';
  const actionLabel = d.action === 'CREATE' ? 'Внесение' : d.action === 'DELETE' ? 'Удаление' : 'Изменение';

  return (
    <Box sx={{
      bgcolor: '#1e293b', color: 'white',
      px: 2, py: 1.5, borderRadius: 2,
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 240,
    }}>
      {/* Строка: дата + действие */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{d.actionDate}</Typography>
        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: actionColor, fontSize: '0.68rem', fontWeight: 700, color: 'white', textTransform: 'uppercase' }}>
          {actionLabel}
        </Box>
      </Box>

      {/* Кто и откуда */}
      <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1', mb: 1 }}>
        {d.actionBy || '—'}{d.cityId ? ` · ${d.cityId}` : ''}
      </Typography>

      {/* Остаток */}
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: 'white', mb: 0.25 }}>
        {formatQtyPcs(d.quantity, d.quantityPcs)}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#38bdf8', mb: d.serialNumber || d.expiryDate || d.comment ? 0.75 : 0 }}>
        {fmtSum(d.totalSum)}
      </Typography>

      {/* Серия и срок */}
      {(d.serialNumber || d.expiryDate) && (
        <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8', mb: d.comment ? 0.5 : 0 }}>
          {d.serialNumber}{d.serialNumber && d.expiryDate ? ' · ' : ''}{d.expiryDate}
        </Typography>
      )}

      {/* Комментарий */}
      {d.comment && (
        <Typography sx={{ fontSize: '0.73rem', color: '#fbbf24', fontStyle: 'italic' }}>
          «{d.comment}»
        </Typography>
      )}
    </Box>
  );
};

// ─── Sub-Component 1: Сравнение аптек (Мультилинейный график по дням) ───────────

interface PharmacyCompareViewProps {
  data: ProductDynamicsRow[];
  c3Product: string | null;
}

const PharmacyCompareView: React.FC<PharmacyCompareViewProps> = ({ data, c3Product }) => {
  const chartMultiData = useMemo(() => {
    const dateSet = new Set<string>();
    const pharmMap = new Map<string, Map<string, number>>();

    data.forEach(r => {
      dateSet.add(r.date);
      if (!pharmMap.has(r.PharmacyNum)) pharmMap.set(r.PharmacyNum, new Map());
      const dm = pharmMap.get(r.PharmacyNum)!;
      dm.set(r.date, (dm.get(r.date) || 0) + r.qty);
    });

    const allPharmEntries = Array.from(pharmMap.entries())
      .map(([pn, dm]) => ({ pn, total: Array.from(dm.values()).reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total);

    const targetPharms = c3Product
      ? allPharmEntries.map(x => x.pn)
      : allPharmEntries.slice(0, 5).map(x => x.pn);

    const dates = Array.from(dateSet).sort();
    return {
      data: dates.map(d => {
        const row: Record<string, any> = {
          date: d,
          label: new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }),
        };
        targetPharms.forEach(pn => { row[`№${pn}`] = pharmMap.get(pn)?.get(d) ?? 0; });
        return row;
      }),
      pharmacies: targetPharms,
    };
  }, [data, c3Product]);

  if (!chartMultiData || chartMultiData.data.length === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={6}>
        Нет данных для сравнения аптек
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {c3Product
          ? `Сравнение аптечных точек по товару «${c3Product}» (${chartMultiData.pharmacies.length} аптек)`
          : 'Сравнение динамики по топ-5 аптекам сети'}
      </Typography>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartMultiData.data} margin={{ top: 10, right: 24, left: 10, bottom: 8 }}>
          <CartesianGrid {...gridStyle} />
          <XAxis dataKey="label" {...axisStyle} />
          <YAxis tickFormatter={v => v.toLocaleString('ru-RU')} {...axisStyle} width={56} />
          <RTooltip content={<QtyTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          {chartMultiData.pharmacies.map((pn, i) => (
            <Line
              key={pn} type="monotone" dataKey={`№${pn}`} name={`Аптека №${pn}`}
              stroke={rainbowColor(i, chartMultiData.pharmacies.length)} strokeWidth={2.5}
              dot={{ r: 4, fill: rainbowColor(i, chartMultiData.pharmacies.length), stroke: 'white', strokeWidth: 2 }}
              activeDot={{ r: 7 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ─── Sub-Component 2: Карточка конкретной аптеки (Плавный график + История операций) ─────────

interface SinglePharmacyViewProps {
  data: ProductDynamicsRow[];
  movements: JournalMovementRow[];
  c3Pharmacy: string;
  c3Product: string | null;
}

const SinglePharmacyView: React.FC<SinglePharmacyViewProps> = ({
  data, movements, c3Pharmacy, c3Product,
}) => {
  const [historyMode, setHistoryMode] = useState<'chart' | 'table'>('chart');

  // 1. Плавный календарный график остатков по дням (YYYY-MM-DD)
  const dailyData = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach(r => {
      if (r.PharmacyNum !== c3Pharmacy) return;
      map.set(r.date, (map.get(r.date) || 0) + r.qty);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, qty]) => ({
        date, qty,
        label: new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }),
      }));
  }, [data, c3Pharmacy]);

  const totalQty = useMemo(() => dailyData.reduce((s, d) => s + d.qty, 0), [dailyData]);

  // 2. Данные хронологического графика операций из ZF_Journal_Movements
  const movementChartData = useMemo((): MovementPoint[] => {
    if (!c3Product || movements.length === 0) return [];
    return movements.map((m, index) => {
      const timeStr = m.actionDate ? m.actionDate.slice(11, 19) : ''; // "13:11:36" с секундами для уникальности
      const dateStr = m.actionDate ? m.actionDate.slice(5, 10) : '';  // "07-28"
      // Нормализуем action прямо здесь, чтобы гарантировать корректный статус везде
      const act = (m.action || '').trim().toUpperCase() as 'CREATE' | 'UPDATE' | 'DELETE';
      const pcsVal = m.quantityPcs ?? 0;
      const qtyVal = m.quantity ?? 0;

      // Логика остатка: для DELETE — 0. Если есть упаковки (>0), берем quantity. 
      // Если упаковки = 0, но есть штуки — берем штуки.
      const val = act === 'DELETE'
        ? 0
        : (qtyVal > 0 ? qtyVal : pcsVal);

      // Уникальный лейбл для X-оси (дата + время с секундами)
      const fullLabel = `${dateStr} ${timeStr}`.trim() || `№${index + 1}`;

      return {
        id: m.id,
        action: act,
        label: fullLabel,
        actionDate: m.actionDate,
        actionBy: m.actionBy || '',
        pharmacyNum: m.pharmacyNum || '',
        cityId: m.cityId || '',
        quantity: qtyVal,
        quantityPcs: pcsVal,
        price: m.price || 0,
        totalSum: m.totalSum || 0,
        serialNumber: m.serialNumber || '',
        expiryDate: m.expiryDate || '',
        producer: m.producer || '',
        comment: m.comment || '',
        val,
      };
    });
  }, [movements, c3Product]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Верхний график: Плавная динамика по дням ── */}
      <Box>
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Аптека №{c3Pharmacy} {c3Product ? `· ${c3Product}` : '· все товары'}
          </Typography>
          <Typography variant="caption" fontWeight={700} color="primary.main">
            {totalQty.toLocaleString('ru-RU')} уп. всего в журнале
          </Typography>
        </Box>

        {dailyData.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={6}>
            Нет данных за выбранный период
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={dailyData} margin={{ top: 10, right: 24, left: 10, bottom: 8 }}>
              <defs>
                <linearGradient id="singleQtyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridStyle} />
              <XAxis dataKey="label" {...axisStyle} />
              <YAxis tickFormatter={v => v.toLocaleString('ru-RU')} {...axisStyle} width={56} />
              <RTooltip content={<QtyTooltip />} cursor={{ stroke: '#10b981', strokeWidth: 1.5, strokeDasharray: '3 3' }} />
              <Area type="monotone" dataKey="qty" name="Кол-во" fill="url(#singleQtyGrad)" stroke="transparent" />
              <Line
                type="monotone" dataKey="qty" name="Кол-во"
                stroke="#10b981" strokeWidth={2.5}
                dot={{ r: dailyData.length > 30 ? 2 : 5, fill: '#10b981', stroke: 'white', strokeWidth: 2 }}
                activeDot={{ r: 7, fill: '#059669', stroke: 'white', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* ── Нижний блок: История операций из ZF_Journal_Movements ── */}
      {c3Product && (
        <Box>
          <Divider sx={{ mb: 2.5 }} />

          {/* Шапка истории операций */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                История операций
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({movements.length} операций)
              </Typography>
            </Box>

            {movements.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Тумблер График / Таблица */}
                <ToggleButtonGroup
                  value={historyMode}
                  exclusive
                  onChange={(_, v) => { if (v) setHistoryMode(v); }}
                  size="small"
                  sx={{
                    height: 32,
                    '& .MuiToggleButton-root': {
                      px: 1.5, py: 0.5,
                      fontSize: '0.78rem', fontWeight: 600,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ToggleButton value="chart">
                    График операций
                  </ToggleButton>
                  <ToggleButton value="table">
                    Таблица логов
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>

          {movements.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc' }}>
              <InventoryIcon sx={{ color: '#94a3b8', fontSize: 32, mb: 1 }} />
              <Typography color="text.secondary" variant="body2">
                Для товара «{c3Product}» в Аптеке №{c3Pharmacy} нет зафиксированных операций в логе.
              </Typography>
            </Paper>
          ) : historyMode === 'chart' ? (
            // ── Вид 1: График хронологии операций ──
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Хронологическая цепочка действий: наведите на точку для детального просмотра реквизитов операции
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={movementChartData} margin={{ top: 10, right: 24, left: 10, bottom: 8 }}>
                  <defs>
                    <linearGradient id="movQtyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridStyle} />
                  <XAxis
                    dataKey="label"
                    tickFormatter={(val: string) => (val && val.length > 11 ? val.slice(0, 11) : val)}
                    {...axisStyle}
                  />
                  <YAxis
                    tickFormatter={v => v.toLocaleString('ru-RU')}
                    {...axisStyle}
                    width={48}
                  />
                  <RTooltip content={<MovementTooltip />} cursor={{ stroke: '#2563eb', strokeWidth: 1.5, strokeDasharray: '3 3' }} />
                  <Area type="monotone" dataKey="val" name="Кол-во" fill="url(#movQtyGrad)" stroke="transparent" />
                  <Line
                    type="monotone" dataKey="val" name="Кол-во"
                    stroke="#2563eb" strokeWidth={2.5}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (!cx || !cy) return null;
                      const p = payload as MovementPoint;
                      const color = p.action === 'DELETE' ? '#ef4444' : p.action === 'CREATE' ? '#16a34a' : '#2563eb';
                      return (
                        <circle
                          key={p.id}
                          cx={cx} cy={cy} r={5}
                          fill={color} stroke="white" strokeWidth={2}
                        />
                      );
                    }}
                    activeDot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (!cx || !cy) return null;
                      const p = payload as MovementPoint;
                      const color = p.action === 'DELETE' ? '#ef4444' : p.action === 'CREATE' ? '#16a34a' : '#2563eb';
                      return (
                        <circle
                          cx={cx} cy={cy} r={8}
                          fill={color} stroke="white" strokeWidth={2.5}
                        />
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          ) : (
            // ── Вид 2: Подробная таблица логов ──
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 360 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f8fafc', color: 'text.secondary', fontSize: '0.78rem' } }}>
                    <TableCell width="140">Дата и время</TableCell>
                    <TableCell width="110">Действие</TableCell>
                    <TableCell width="110">Аптека</TableCell>
                    <TableCell width="140">Код / Серия</TableCell>
                    <TableCell width="110">Годность</TableCell>
                    <TableCell width="150" align="right">Остаток (уп. / шт.)</TableCell>
                    <TableCell width="100" align="right">Дельта</TableCell>
                    <TableCell width="110" align="right">Цена / Сумма</TableCell>
                    <TableCell width="110" align="right">Дельта суммы</TableCell>
                    <TableCell>Комментарий</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((m) => {
                    const act = (m.action || '').trim().toUpperCase();
                    const isCreate = act === 'CREATE';
                    const isDelete = act === 'DELETE';

                    const actionColorHex = isCreate ? '#16a34a' : isDelete ? '#dc2626' : '#2563eb';
                    const actionLabel = isCreate ? 'Внесение' : isDelete ? 'Удаление' : 'Изменение';

                    const qtyPlus = m.quantityDelta > 0;
                    const qtyMinus = m.quantityDelta < 0;
                    const sumPlus = m.totalSumDelta > 0;
                    const sumMinus = m.totalSumDelta < 0;

                    return (
                      <TableRow key={m.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
                          {m.actionDate}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: actionColorHex }}>
                          {actionLabel}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {m.actionBy || '—'}
                          {m.cityId ? <Typography variant="caption" display="block" color="text.secondary">{m.cityId}</Typography> : null}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {m.nomenclatureCode ? <strong>{m.nomenclatureCode}</strong> : null}
                          {m.serialNumber ? <Typography variant="caption" display="block" color="text.secondary">{m.serialNumber}</Typography> : '—'}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {m.expiryDate || '—'}
                        </TableCell>

                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                          {formatQtyPcs(m.quantity, m.quantityPcs)}
                        </TableCell>

                        <TableCell align="right" sx={{
                          fontSize: '0.8rem', fontWeight: 700,
                          color: qtyPlus ? '#16a34a' : qtyMinus ? '#dc2626' : 'text.primary',
                        }}>
                          {qtyPlus ? `+${m.quantityDelta}` : m.quantityDelta} уп.
                        </TableCell>

                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                          {fmtSum(m.totalSum)}
                          {m.price ? <Typography variant="caption" display="block" color="text.secondary">Цена: {fmtSum(m.price)}</Typography> : null}
                        </TableCell>

                        <TableCell align="right" sx={{
                          fontSize: '0.8rem', fontWeight: 700,
                          color: sumPlus ? '#16a34a' : sumMinus ? '#dc2626' : 'text.primary',
                        }}>
                          {sumPlus ? `+${fmtSum(m.totalSumDelta)}` : sumMinus ? `-${fmtSum(Math.abs(m.totalSumDelta))}` : fmtSum(m.totalSumDelta)}
                        </TableCell>

                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.comment || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Main Orchestrator Component ──────────────────────────────────────────────

/**
 * Главный компонент вкладки «Динамика по товару».
 */
const ProductDynamicsChart: React.FC<ProductDynamicsChartProps> = ({
  data, movements = [], pharmacies, products,
  c3Pharmacy, setC3Pharmacy, c3Product, setC3Product,
}) => {
  // Локальный список товаров для выбранной аптеки (каскадная фильтрация)
  const [pharmacyProducts, setPharmacyProducts] = useState<string[] | null>(null);
  const [loadingPharmacyProducts, setLoadingPharmacyProducts] = useState(false);

  // Каскадная подгрузка товаров при смене c3Pharmacy
  useEffect(() => {
    if (c3Pharmacy === 'all') {
      setPharmacyProducts(null);
      return;
    }
    setLoadingPharmacyProducts(true);
    const username = localStorage.getItem('username') ?? undefined;
    axios.get(`${API_BASE}/journal/filter-options`, {
      params: { username, pharmacyNum: c3Pharmacy },
    })
      .then(resp => {
        if (resp.data?.success) {
          const prods: string[] = resp.data.data.products || [];
          setPharmacyProducts(prods);
        }
      })
      .catch(e => console.error('Error fetching pharmacy products:', e))
      .finally(() => setLoadingPharmacyProducts(false));
  }, [c3Pharmacy]);

  // Доступный список товаров: отфильтрованный по аптеке или общий
  const availableProducts = c3Pharmacy !== 'all' && pharmacyProducts !== null
    ? pharmacyProducts
    : products;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Заголовок + локальные фильтры ── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Динамика по товару</Typography>
        <Typography variant="caption" color="text.secondary">
          изменение количества и история операций
        </Typography>
      </Box>

      {/* Панель фильтров */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Аптека — Autocomplete */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={['all', ...pharmacies]}
          value={c3Pharmacy}
          onChange={(_, v) => {
            const nextPharm = v || 'all';
            setC3Pharmacy(nextPharm);
            if (c3Product) setC3Product(null);
          }}
          getOptionLabel={o => (o === 'all' ? 'Все аптеки (сравнение)' : `Аптека №${o}`)}
          renderInput={params => (
            <TextField {...params} label="Аптека" placeholder="Введите номер…" />
          )}
          noOptionsText="Не найдено"
          clearText="Сбросить"
          openText="Открыть"
          closeText="Закрыть"
          ListboxProps={{ style: { maxHeight: 280 } }}
        />

        {/* Товар — Autocomplete */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 320 }}
          options={availableProducts}
          value={c3Product}
          onChange={(_, v) => setC3Product(v)}
          loading={loadingPharmacyProducts}
          renderInput={params => (
            <TextField
              {...params}
              label="Товар (необязательно)"
              placeholder={loadingPharmacyProducts ? 'Загрузка товаров…' : 'Введите наименование…'}
            />
          )}
          noOptionsText={c3Pharmacy !== 'all' ? 'У этой аптеки нет введённых товаров' : 'Не найдено'}
          clearText="Сбросить"
          openText="Открыть"
          closeText="Закрыть"
          ListboxProps={{ style: { maxHeight: 280 } }}
        />

        {(c3Pharmacy !== 'all' || c3Product) && (
          <Button
            size="small" variant="text" color="inherit" startIcon={<ClearIcon />}
            onClick={() => { setC3Pharmacy('all'); setC3Product(null); }}
          >
            Сбросить
          </Button>
        )}
      </Box>

      {/* ── Рендеринг одного из двух подкомпонентов ── */}
      {c3Pharmacy === 'all' ? (
        <PharmacyCompareView data={data} c3Product={c3Product} />
      ) : (
        <SinglePharmacyView
          data={data}
          movements={movements}
          c3Pharmacy={c3Pharmacy}
          c3Product={c3Product}
        />
      )}
    </Box>
  );
};

export default ProductDynamicsChart;
